"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.InvoiceComponent = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const useInvoice_1 = require("./context/useInvoice");
const OrderDetailSkeleton_1 = require("../../components/order/OrderDetailSkeleton");
const useIsMobile_1 = require("../../hooks/mobile/useIsMobile");
const react_i18next_1 = require("react-i18next");
const OrderInvoiceLayout_1 = require("../../components/order/OrderInvoiceLayout");
const GenericButton_1 = require("../../components/web-view/button/GenericButton");
const UserForm_1 = require("./UserForm");
const toast_1 = require("primereact/toast");
const invoice_module_scss_1 = __importDefault(require("./invoice.module.scss"));
const PadelLoading_1 = require("../../components/loader/PadelLoading");
const invoiceHelpers_1 = require("./utils/invoiceHelpers");
const ErrorPage_1 = __importDefault(require("../../components/error-page/ErrorPage"));
const pdfExporterFactory_1 = require("../../common/utils/pdf-export/pdfExporterFactory");
const ApiPdfInvoiceRepo_1 = require("../../modules/pdfInvoice/infra/ApiPdfInvoiceRepo");
const getById_1 = require("../../modules/pdfInvoice/app/getById");
const pdfRepo = (0, ApiPdfInvoiceRepo_1.createApiPdfInvoiceRepo)();
const InvoiceComponent = ({ id }) => {
    const [invoice, setInvoice] = (0, react_1.useState)(null);
    const [openFormModal, setOpenFormModal] = (0, react_1.useState)(false);
    const { t } = (0, react_i18next_1.useTranslation)();
    const isMobile = (0, useIsMobile_1.useIsMobile)();
    const { getById, update } = (0, useInvoice_1.useInvoice)();
    const toast = (0, react_1.useRef)(null);
    const [error, setError] = (0, react_1.useState)(null);
    const [originalInvoice, setOriginalInvoice] = (0, react_1.useState)(null);
    const [isUpdated, setIsUpdated] = (0, react_1.useState)(false);
    const [isSaving, setIsSaving] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        if (id) {
            fetchInvoice(id);
        }
    }, [id, isUpdated]);
    const fetchInvoice = async (invoiceId) => {
        try {
            const data = await getById(invoiceId);
            if (data) {
                const prepared = (0, invoiceHelpers_1.prepareInvoiceData)(data);
                setInvoice(prepared);
                setOriginalInvoice(prepared);
                setError(null);
            }
            else {
                console.error('PdfInvoice not found:', invoiceId);
                throw new Error(t('invoice.notFoundMessage'));
            }
        }
        catch (error) {
            console.error('Error fetching invoice:', error);
            setError(t('invoiceFetch.invoiceFetchError'));
        }
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (!invoice)
            return;
        if (['name', 'taxId', 'email', 'phone'].includes(name)) {
            setInvoice((prev) => ({
                ...prev,
                customer: {
                    ...prev.customer,
                    [name]: value,
                },
            }));
            return;
        }
        if (name.startsWith('address.')) {
            const field = name.split('.')[1];
            setInvoice((prev) => ({
                ...prev,
                customer: {
                    ...prev.customer,
                    address: {
                        ...prev.customer.address,
                        [field]: value,
                    },
                },
            }));
            return;
        }
    };
    const handleCancel = () => {
        if (originalInvoice) {
            setInvoice(originalInvoice);
        }
        setOpenFormModal(false);
    };
    const showEditButton = (0, invoiceHelpers_1.canEditClientData)(invoice) && invoice && !openFormModal;
    const saveInvoice = async () => {
        if (!invoice)
            return;
        const missingFields = (0, invoiceHelpers_1.validateInvoiceFields)(invoice, t);
        if (missingFields.length > 0) {
            toast.current?.show({
                severity: 'warn',
                summary: t('requiredFields'),
                detail: `${t('completeFields')}: ${missingFields.join(', ')}`,
                life: 4000,
            });
            return;
        }
        const validationErrors = (0, invoiceHelpers_1.validateFieldFormats)(invoice);
        if (validationErrors.length > 0) {
            toast.current?.show({
                severity: 'warn',
                summary: t('validation.validationErrors'),
                detail: validationErrors.join(', '),
                life: 4000,
            });
            return;
        }
        setIsSaving(true);
        try {
            await update(invoice);
            setOriginalInvoice(invoice);
            setOpenFormModal(false);
            toast.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: t('validation.successMessage'),
                life: 3000,
            });
            setIsUpdated(true);
        }
        catch (error) {
            console.error('Error saving invoice:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: t('validation.errorMessage'),
                life: 3000,
            });
        }
        finally {
            setIsSaving(false);
        }
    };
    const exportPdf = async () => {
        if (!invoice?.id)
            return;
        try {
            const invoices = await (0, getById_1.getById)(pdfRepo, invoice.id);
            if (!invoices || invoices.length === 0) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Info',
                    detail: t('noDataToExport'),
                    life: 5000,
                });
                return;
            }
            (0, pdfExporterFactory_1.exportToPDF)(invoices, t, 'pdfInvoices');
        }
        catch (err) {
            console.error('Error exporting PDF:', err);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: t('errorExportingPdf'),
                life: 5000,
            });
        }
    };
    if (error) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "pt-20 pl-1 flex justify-items-center", children: (0, jsx_runtime_1.jsx)("div", { className: "flex-1 overflow-y-auto pb-32", children: (0, jsx_runtime_1.jsx)(ErrorPage_1.default, { message: error }) }) }));
    }
    if (!invoice) {
        return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(toast_1.Toast, { className: isMobile ? 'w-3/4 text-xs' : 'w-full', ref: toast }), (0, jsx_runtime_1.jsx)(OrderDetailSkeleton_1.OrderDetailSkeleton, { isMobile: isMobile })] }));
    }
    const showUserForm = openFormModal || (0, invoiceHelpers_1.hasClientData)(invoice);
    const alertMessage = (0, invoiceHelpers_1.buildAlertMessage)({ invoice, isMobile, t });
    return ((0, jsx_runtime_1.jsxs)("div", { "data-testid": "invoice-component", className: "pt-5 pl-1 h-screen flex flex-col relative", children: [(0, jsx_runtime_1.jsx)(toast_1.Toast, { className: isMobile ? 'w-3/4 text-xs' : 'w-full', ref: toast }), isSaving && ((0, jsx_runtime_1.jsx)("div", { className: "absolute inset-0 flex justify-center items-center z-50", style: { backgroundColor: 'rgb(66 69 81 / 19%)' }, children: (0, jsx_runtime_1.jsx)(PadelLoading_1.PadelLoading, {}) })), (0, jsx_runtime_1.jsx)("div", { className: "flex-1 overflow-y-auto pb-32", children: (0, jsx_runtime_1.jsx)(OrderInvoiceLayout_1.OrderInvoiceLayout, { orderDetail: invoice, alertMessage: alertMessage, renderExtraContent: showUserForm && ((0, jsx_runtime_1.jsx)(UserForm_1.UserForm, { invoice: invoice, handleInputChange: handleInputChange, openFormModal: openFormModal })), rightContent: openFormModal ? ((0, jsx_runtime_1.jsxs)("div", { className: "flex gap-2", children: [(0, jsx_runtime_1.jsx)(GenericButton_1.GenericButton, { label: t('cancel'), onClick: handleCancel, className: invoice_module_scss_1.default.buttonContainer, disabled: isSaving }), (0, jsx_runtime_1.jsx)(GenericButton_1.GenericButton, { label: t('save'), onClick: saveInvoice, className: invoice_module_scss_1.default.buttonContainer, disabled: isSaving })] })) : ((0, jsx_runtime_1.jsxs)("div", { className: "flex gap-2", children: [showEditButton && ((0, jsx_runtime_1.jsx)(GenericButton_1.GenericButton, { label: (0, invoiceHelpers_1.hasClientData)(invoice) ? t('edit') : t('addDataClient'), onClick: () => setOpenFormModal(true), className: invoice_module_scss_1.default.buttonContainer })), !isMobile && ((0, jsx_runtime_1.jsx)(GenericButton_1.GenericButton, { label: t('exportToPDF'), onClick: exportPdf, className: invoice_module_scss_1.default.buttonContainer }))] })) }) })] }));
};
exports.InvoiceComponent = InvoiceComponent;
